import React, { useEffect, useState } from 'react';
import PageHeader from '../components/PageHeader';
import { supabase } from '../lib/admin-supabase';
import { Link } from 'react-router-dom';
import { MapPin, ArrowRight, Folder } from 'lucide-react';

interface Emenda {
  id: string;
  titulo: string;
  resumo?: string;
  valor_emenda?: number;
  imagem_capa?: string;
  status: string;
  created_at: string;
  categoria?: string;
  municipio_beneficiado?: string;
  estado?: string;
}

const Emendas = () => {
  const [emendas, setEmendas] = useState<Emenda[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchEmendas = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error } = await supabase
          .from('emendas')
          .select('*')
          .eq('status', 'ativo')
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Erro ao buscar emendas:', error);
          setError('Erro ao carregar emendas');
        } else {
          setEmendas(data || []);
        }
      } catch (err) {
        console.error('Erro ao buscar emendas:', err);
        setError('Erro ao carregar emendas');
      } finally {
        setLoading(false);
      }
    };
    fetchEmendas();
  }, []);

  const formatarData = (data: string) => {
    return new Date(data).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatarMoeda = (valor: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  return (
    <main className="bg-slate-50 min-h-screen pb-24">
      <PageHeader
        title="Emendas Parlamentares"
        description="Transparência e compromisso: acompanhe os recursos e projetos destinados ao desenvolvimento da nossa região."
      />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10">
        {error && (
          <div className="mb-8 p-6 bg-white border-l-4 border-red-500 rounded-2xl shadow-sm">
            <p className="text-red-700 font-medium text-center">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {loading ? (
            Array.from({ length: 6 }).map((_, idx) => (
              <div key={idx} className="bg-white h-[450px] rounded-[32px] shadow-sm animate-pulse border border-slate-100" />
            ))
          ) : emendas.length === 0 ? (
            <div className="col-span-full text-center py-32 bg-white rounded-[40px] border border-dashed border-slate-300">
              <div className="max-w-md mx-auto">
                <div className="w-24 h-24 bg-slate-100 rounded-full mx-auto mb-6 flex items-center justify-center">
                  <Folder className="w-10 h-10 text-slate-400" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">Nenhuma emenda publicada</h3>
                <p className="text-slate-500">As emendas carimbadas aparecerão aqui em breve.</p>
              </div>
            </div>
          ) : (
            emendas.map((emenda) => (
              <article
                key={emenda.id}
                className="group bg-white rounded-[32px] border border-slate-100 shadow-sm hover:shadow-xl hover:shadow-blue-900/5 transition-all duration-500 overflow-hidden flex flex-col h-full"
              >
                {/* Imagem de Capa */}
                <div className="relative h-60 overflow-hidden">
                  {emenda.imagem_capa ? (
                    <img
                      src={emenda.imagem_capa}
                      alt={emenda.titulo}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center">
                      <Folder className="w-16 h-16 text-white/20" />
                    </div>
                  )}
                  {emenda.categoria && (
                    <div className="absolute top-4 left-4">
                      <span className="px-4 py-1.5 bg-white/95 backdrop-blur-sm text-blue-700 text-xs font-bold rounded-full shadow-sm">
                        {emenda.categoria}
                      </span>
                    </div>
                  )}
                </div>

                <div className="p-8 flex flex-col flex-1">
                  {/* Localização e Valor */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center text-slate-400 text-sm font-medium">
                      <MapPin className="w-4 h-4 mr-1 text-blue-500" />
                      {emenda.municipio_beneficiado || 'Região'} - {emenda.estado || 'BR'}
                    </div>
                    {emenda.valor_emenda && (
                      <div className="text-emerald-600 font-bold text-sm">
                        {formatarMoeda(emenda.valor_emenda)}
                      </div>
                    )}
                  </div>

                  {/* Título */}
                  <h3 className="text-xl font-extrabold text-slate-900 mb-3 line-clamp-2 leading-tight group-hover:text-blue-600 transition-colors">
                    {emenda.titulo}
                  </h3>

                  {/* Descrição Resumida */}
                  <p className="text-slate-500 leading-relaxed mb-6 line-clamp-3 text-sm">
                    {emenda.resumo || 'Clique em saiba mais para ver os detalhes completos deste projeto e baixar os documentos oficiais.'}
                  </p>

                  <div className="mt-auto pt-6 border-t border-slate-50 flex items-center justify-between">
                    <span className="text-xs text-slate-300 font-medium uppercase tracking-wider">
                      {formatarData(emenda.created_at)}
                    </span>

                    <Link
                      to={`/emendas/${emenda.id}`}
                      className="inline-flex items-center text-blue-600 font-bold text-sm hover:translate-x-1 transition-transform"
                    >
                      Saiba Mais
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Link>
                  </div>
                </div>
              </article>
            ))
          )}
        </div>
      </section>
    </main>
  );
};

export default Emendas;
